import { createContext, useCallback, useState } from 'react';
import mentorsAPI, { Mentor, MentorId } from '../api/mentors';
import { Specialization } from '../api/specializations';

export interface MentorContextProps {
  mentor?: Mentor;
  mentorSpecializations?: Specialization['id'][];
  updateMentor: (mentor: Mentor) => any;
  updateSpecializations: (specializations: Specialization['id'][]) => any;
  loadMentor: (mentorId: MentorId) => Promise<any>;
  loadMentorSpecializations: (mentorId: MentorId) => Promise<any>;
  isMentorLoading: boolean;
  isSpecializationsLoading: boolean;
  isAccessDenied: boolean;
}

export const MentorContext = createContext<MentorContextProps>({
  updateMentor: () => {},
  updateSpecializations: () => {},
  loadMentor: async () => {},
  loadMentorSpecializations: async () => {},
  isMentorLoading: true,
  isSpecializationsLoading: true,
  isAccessDenied: false,
});

interface MentorProviderProps {
  children: React.ReactNode;
}

export const MentorProvider = ({ children }: MentorProviderProps) => {
  const [mentor, setMentor] = useState<Mentor>();
  const [mentorSpecializations, setMentorSpecializations] =
    useState<Specialization['id'][]>();
  const [isMentorLoading, setIsMentorLoading] = useState(true);
  const [isSpecializationsLoading, setIsSpecializationsLoading] =
    useState(true);
  const [isAccessDenied, setIsAccessDenied] = useState(false);

  const updateMentor = useCallback((mentor: Mentor) => {
    setMentor(mentor);
  }, []);

  const updateSpecializations = useCallback(
    (specializations: Specialization['id'][]) => {
      setMentorSpecializations(specializations);
    },
    [],
  );

  const loadMentor = useCallback(async (mentorId: MentorId) => {
    try {
      setIsMentorLoading(true);
      const loadedMentor = await mentorsAPI.getMentor(mentorId);
      setMentor(loadedMentor);
      setIsMentorLoading(false);
    } catch (e: any) {
      if (e.response?.status === 403) {
        setIsAccessDenied(true);
      }
      setIsMentorLoading(false);
      console.error('error loadMentor', e);
    }
  }, []);

  const loadMentorSpecializations = useCallback(async (mentorId: MentorId) => {
    try {
      setIsSpecializationsLoading(true);
      const specializations = await mentorsAPI.getMentorSpecializations(
        mentorId,
      );
      setMentorSpecializations(specializations.map((spec) => spec.id));
      setIsSpecializationsLoading(false);
    } catch (e: any) {
      setIsSpecializationsLoading(false);
      console.error('error loadMentorSpecializations', e);
    }
  }, []);

  const contextValue: MentorContextProps = {
    mentor,
    isMentorLoading,
    isAccessDenied,
    loadMentor,
    updateMentor,
    mentorSpecializations,
    isSpecializationsLoading,
    loadMentorSpecializations,
    updateSpecializations,
  };

  return (
    <MentorContext.Provider value={contextValue}>
      {children}
    </MentorContext.Provider>
  );
};
